import { createAsyncThunk } from '@reduxjs/toolkit';
import { showToast } from '@/store/notifications/slice';
import chevron from '@/services/chevron';
import { isNetworkError } from '@/utils/helperFunc';
import { captureSentryException } from '@/utils/sentry';
import type { CreateUpdateGlobalStyleRequest, GlobalStyle } from './types';
import { updatePageById } from '../pages/thunks';
import type { PageResponse } from '../pages/types';
import { setActiveGlobalStyle } from './slice';
import { selectUserAccessId } from '@/store/user/selectors';
import type { RootState } from '../store';
import { runStyleJsonMigrations } from '@builder4/shared/migrations/style';
import { validateStylesConfig } from '@builder4/shared/dto/styles/validateStylesConfig';

// GET → fetch global style by id
export const fetchGlobalStyleById = createAsyncThunk<GlobalStyle, string>(
  'globalStyle/fetchGlobalStyleById',
  async (styleId: string, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/global-styles/${styleId}`;
    try {
      const response = (await chevron.http({
        url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })) as GlobalStyle;

      const migrationResult = runStyleJsonMigrations(response.globalStyle);
      if (migrationResult.error) {
        const error = new Error(`Migration error: ${migrationResult.errorMessage}`);
        captureSentryException(error, {
          tags: { feature: 'global-style-loading', operation: 'migration' },
        });
        console.error('Migration error: ', migrationResult.errorMessage);
        throw error;
      }
      const validationResult = validateStylesConfig(migrationResult.json);
      if (!validationResult.isValid) {
        const error = new Error('Invalid global style');
        captureSentryException(error, {
          tags: { feature: 'global-style-loading', operation: 'validation' },
        });
        console.error('Invalid global style: ', validationResult.errorMessages);
        throw error;
      }

      return {
        ...response,
        globalStyle: migrationResult.json,
      };
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot reload. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Error fetching data. Please try again', state: 'error' }));
      }
      throw error;
    }
  },
);

// POST → create global style
export const createGlobalStyle = createAsyncThunk<
  GlobalStyle,
  CreateUpdateGlobalStyleRequest & { showSuccessToast?: boolean }
>('globalStyle/create', async ({ showSuccessToast = true, ...body }, { dispatch, getState }) => {
  const url = `${import.meta.env.VITE_API_STARGATE}/builder4/global-styles`;
  try {
    let response = (await chevron.http({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    })) as GlobalStyle;

    const accessId = selectUserAccessId(getState() as RootState);
    const ownerId = accessId ?? response.ownerId;
    const createdAt = response.createdAt ?? new Date().toISOString();

    response = {
      ...response,
      ownerId,
      createdAt,
      updatedAt: response.updatedAt ?? createdAt,
      globalStyle: body.global_style,
      label: body.label,
      published: false,
    };

    if (showSuccessToast) {
      if (body.isDuplicate) {
        dispatch(showToast({ text: 'Global style duplicated successfully!', state: 'success' }));
      } else {
        dispatch(showToast({ text: 'Global style created successfully!', state: 'success' }));
      }
    }

    return response;
  } catch (error) {
    const isNetworkErr = isNetworkError(error);

    if (isNetworkErr) {
      // For network errors, show a toast but the global error page will also show
      dispatch(
        showToast({ text: 'Cannot create global style. Check connection.', state: 'warning' }),
      );
    } else {
      // For non-network errors, show error toast
      dispatch(
        showToast({ text: 'Failed to create global style. Please try again.', state: 'error' }),
      );
    }
    throw error;
  }
});

// GET → fetch global style list
export const fetchGlobalStyleList = createAsyncThunk<GlobalStyle[]>(
  'globalStyle/fetchGlobalStyleList',
  async (_, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/global-styles`;
    try {
      const response = (await chevron.http({
        url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })) as GlobalStyle[];

      for (let i = response.length - 1; i >= 0; i--) {
        const style = response[i];
        const migrationResult = runStyleJsonMigrations(style.globalStyle);
        if (migrationResult.error) {
          const error = new Error(`Migration error: ${migrationResult.errorMessage}`);
          captureSentryException(error, {
            tags: { feature: 'global-style-loading', operation: 'migration' },
          });
          console.error('Migration error: ', migrationResult.errorMessage);
          response.splice(i, 1);
          continue;
        }
        const validationResult = validateStylesConfig(migrationResult.json);
        if (!validationResult.isValid) {
          const error = new Error('Invalid global style');
          captureSentryException(error, {
            tags: { feature: 'global-style-loading', operation: 'validation' },
          });
          console.error('Invalid global style: ', validationResult.errorMessages);
          response.splice(i, 1);
          continue;
        }
        style.globalStyle = migrationResult.json;
      }

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot load pages. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Failed to load pages. Please try again.', state: 'error' }));
      }
      throw error;
    }
  },
);

// PATCH → update global style by id
export const updateGlobalStyleById = createAsyncThunk<
  GlobalStyle,
  { styleId: string; body: CreateUpdateGlobalStyleRequest; showSuccessToast?: boolean }
>(
  'globalStyle/updateGlobalStyleById',
  async ({ styleId, body, showSuccessToast = true }, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/global-styles/${styleId}`;
    try {
      let response = (await chevron.http({
        url,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body,
      })) as GlobalStyle;

      response = {
        ...response,
        label: body.label,
        globalStyle: body.global_style,
        published: body.published ?? response.published,
      };

      if (showSuccessToast) {
        dispatch(
          showToast({
            text: 'Changes saved successfully',
            state: 'success',
          }),
        );
      }

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot save. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Save failed. Please try again.', state: 'error' }));
      }
      throw error;
    }
  },
);

// Delete → global style by id
export const deleteGlobalStyleById = createAsyncThunk<{ id: string }, string>(
  'globalStyle/deleteGlobalStyleById',
  async (styleId, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/global-styles/${styleId}`;
    try {
      let response = (await chevron.http({
        url,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })) as { id: string };
      response = {
        id: styleId,
      };
      dispatch(
        showToast({
          text: 'Style deleted',
          state: 'success',
        }),
      );

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot delete. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Cannot delete. Please try again.', state: 'error' }));
      }
      throw error;
    }
  },
);

// PATCH → update page's global style id
export const applyGlobalStyle = createAsyncThunk<
  PageResponse,
  { pageId: string; globalStyleId: string; showSuccessToast?: boolean }
>(
  'globalStyle/applyGlobalStyle',
  async ({ pageId, globalStyleId, showSuccessToast = true }, { dispatch }) => {
    try {
      const result = await dispatch(
        updatePageById({
          pageId,
          body: { global_style_id: globalStyleId },
        }),
      ).unwrap();

      if (result?.globalStyle?.id) {
        // migrate client-side so the style is always at a version this app understands
        const migrationResult = runStyleJsonMigrations(result.globalStyle.globalStyle);
        if (!migrationResult.error) {
          const validationResult = validateStylesConfig(migrationResult.json);
          if (validationResult.isValid) {
            dispatch(
              setActiveGlobalStyle({
                ...result.globalStyle,
                globalStyle: migrationResult.json,
              }),
            );
          } else {
            dispatch(setActiveGlobalStyle(result.globalStyle));
          }
        } else {
          dispatch(setActiveGlobalStyle(result.globalStyle));
        }
      }

      if (showSuccessToast) {
        dispatch(
          showToast({
            text: 'Global style applied successfully',
            state: 'success',
          }),
        );
      }

      return result;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot apply style. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(
          showToast({ text: 'Failed to apply global style. Please try again.', state: 'error' }),
        );
      }
      throw error;
    }
  },
);
